import { Clock, Phone, Mail, MessageCircle, Check, Sparkles, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";
import { OrderFlowLogo } from "@/components/OrderFlowLogo";

interface TierConfig {
  id: "starter" | "growth" | "unlimited";
  isRecommended?: boolean;
  features: Array<{
    labelKey: string;
    quantityKey?: string;
  }>;
}

const TIER_CONFIGS: TierConfig[] = [
  {
    id: "starter",
    features: [
      { labelKey: "orderManagement" },
      { labelKey: "customerTracking" },
      { labelKey: "deliveryLink", quantityKey: "one" },
      { labelKey: "intakeForm", quantityKey: "one" },
      { labelKey: "deliveryFlow" },
      { labelKey: "logsHistory" },
    ],
  },
  {
    id: "growth",
    isRecommended: true,
    features: [
      { labelKey: "everything" },
      { labelKey: "deliveryLinks", quantityKey: "upToFive" },
      { labelKey: "intakeForms", quantityKey: "upToFive" },
    ],
  },
  {
    id: "unlimited",
    features: [
      { labelKey: "unlimitedLinks" },
      { labelKey: "unlimitedForms" },
      { labelKey: "noCeiling" },
    ],
  },
];

const FAQ_KEYS = ["usage", "exceed", "change", "links", "starterLimits", "cancel"] as const;

export function TrialExpired() {
  const { t } = useLanguage();

  const handleContact = (method: "email" | "phone" | "whatsapp") => {
    switch (method) {
      case "email":
        window.location.href = "mailto:info@orderflow.ge?subject=Trial%20Subscription";
        break;
      case "phone":
        window.location.href = "tel:+995505051685";
        break;
      case "whatsapp":
        window.open("https://wa.me/995505051685", "_blank");
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 flex flex-col">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-center">
          <OrderFlowLogo className="h-8" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 container mx-auto px-4 py-12 max-w-6xl">
        {/* Trial Expired Notice */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-100 dark:bg-rose-950/30 mb-6">
            <Clock className="h-8 w-8 text-rose-600 dark:text-rose-400" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-3">
            {t("trialExpired.title")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-lg mx-auto">
            {t("trialExpired.description")}
          </p>
        </div>

        {/* Pricing Header */}
        <div className="text-center space-y-3 mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            {t("landing.pricing.headline")}
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
            {t("landing.pricing.subheadline")}
          </p>
        </div>

        {/* Tier Cards - M3 Style */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-10">
          {TIER_CONFIGS.map((tier) => (
            <Card
              key={tier.id}
              className={`relative flex flex-col overflow-hidden transition-all duration-200 hover:shadow-lg ${
                tier.isRecommended
                  ? "border-primary/60 ring-1 ring-primary/20 bg-card"
                  : "border-border/50 bg-card/80"
              }`}
            >
              {/* Recommended Badge */}
              {tier.isRecommended && (
                <div className="absolute top-0 right-0">
                  <Badge
                    variant="default"
                    className="rounded-none rounded-bl-lg px-3 py-1 text-xs font-medium bg-primary text-primary-foreground"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    {t(`landing.pricing.${tier.id}.badge`)}
                  </Badge>
                </div>
              )}

              <CardHeader className="pb-4 pt-6">
                {/* Price */}
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl sm:text-5xl font-bold text-foreground">
                    {t(`landing.pricing.${tier.id}.price`)}
                  </span>
                  <span className="text-lg text-muted-foreground">
                    {t("landing.pricing.currency")}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">
                    {t("landing.pricing.period")}
                  </span>
                </div>

                {/* Tier Name */}
                <h3 className="text-lg font-semibold text-foreground mt-2">
                  {t(`landing.pricing.${tier.id}.name`)}
                </h3>

                {/* Tagline */}
                <p className="text-sm text-muted-foreground">
                  {t(`landing.pricing.${tier.id}.tagline`)}
                </p>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col pt-0">
                {/* Description */}
                <p className="text-sm text-foreground/80 mb-4">
                  {t(`landing.pricing.${tier.id}.description`)}
                </p>

                {/* Feature List */}
                <ul className="space-y-2.5 flex-1">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground/90">
                        {feature.quantityKey && (
                          <span className="font-medium">
                            {t(`landing.pricing.quantity.${feature.quantityKey}`)}{" "}
                          </span>
                        )}
                        {t(`landing.pricing.features.${feature.labelKey}`)}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Capacity Line */}
                <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border/30">
                  {t(`landing.pricing.${tier.id}.capacity`)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Builder Section */}
        <div className="bg-card/60 backdrop-blur-sm rounded-xl border border-border/30 p-6 sm:p-8 mb-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <Info className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              {t("landing.pricing.trust.headline")}
            </h3>
          </div>
          <ul className="space-y-2 ml-11">
            <li className="flex items-center gap-2 text-sm text-foreground/80">
              <span className="text-primary">•</span>
              {t("landing.pricing.trust.point1")}
            </li>
            <li className="flex items-center gap-2 text-sm text-foreground/80">
              <span className="text-primary">•</span>
              {t("landing.pricing.trust.point2")}
            </li>
            <li className="flex items-center gap-2 text-sm text-foreground/80">
              <span className="text-primary">•</span>
              {t("landing.pricing.trust.point3")}
            </li>
          </ul>
        </div>

        {/* Emotional Tagline */}
        <p className="text-center text-sm sm:text-base text-muted-foreground italic mb-10">
          "{t("landing.pricing.tagline")}"
        </p>

        {/* FAQ Accordion */}
        <div className="max-w-2xl mx-auto mb-12">
          <h3 className="text-lg font-semibold text-foreground mb-4 text-center">
            {t("landing.pricing.faq.label")}
          </h3>
          <Accordion type="single" collapsible className="w-full">
            {FAQ_KEYS.map((key) => (
              <AccordionItem key={key} value={key} className="border-border/50">
                <AccordionTrigger className="text-sm text-left hover:no-underline py-4">
                  {t(`landing.pricing.faq.items.${key}.q`)}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4">
                  {t(`landing.pricing.faq.items.${key}.a`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Contact Section */}
        <div className="bg-muted/50 rounded-xl p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">
            {t("trialExpired.contact.title")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t("trialExpired.contact.description")}
          </p>
          
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              variant="default"
              size="lg"
              onClick={() => handleContact("email")}
              className="gap-2 min-h-[48px]"
            >
              <Mail className="h-4 w-4" />
              {t("trialExpired.contact.email")}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => handleContact("phone")}
              className="gap-2 min-h-[48px]"
            >
              <Phone className="h-4 w-4" />
              {t("trialExpired.contact.phone")}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => handleContact("whatsapp")}
              className="gap-2 min-h-[48px]"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-6 text-center text-sm text-muted-foreground">
        <p>{t("trialExpired.footer")}</p>
      </footer>
    </div>
  );
}